import { useContext } from 'react'

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faStar } from '@fortawesome/pro-light-svg-icons'

import {
  Button, Form, LoadingModule, ModalContext, SidebarModal,
} from '@domain-group/suit-theme'

import useCampaign from '@hooks/useCampaign'
import useCurrentUser from '@hooks/useCurrentUser'
import usePriorityCampaign, { usePriorityCampaignForm } from '@hooks/usePriorityCampaign'
import usePriorityCampaigns from '@hooks/usePriorityCampaigns'

import EntitySelector from '@components/EntitySelector'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import PriorityCampaignUserListItem from './components/PriorityCampaignUserListItem'

const MODAL_KEY = 'ManageCampaignPrioritySettingsModal'

type ModalCallbacks = {
  closeModal: () => void,
  createPriorityCampaign: (payload: HandleCallbackActionParams) => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const ManageCampaignPrioritySettingsModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const { closeModal, createPriorityCampaign } = callbacks || {}

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const modalPayload = modalData?.[MODAL_KEY] ?? {}
  const { campaign: initCampaign } = modalPayload

  const currentUserPayload = useCurrentUser()

  const { currentUser } = currentUserPayload

  const { campaign } = useCampaign(initCampaign)

  const {
    callbacks: {
      createPriorityCampaign: createFn,
    },
    creating,
  } = usePriorityCampaign()

  const priorityCampaignsPayload = usePriorityCampaigns({
    filters: {
      campaign_id: campaign.id,
      owner_type: 'User',
    },
    performHttpRequests: !!campaign.id,
    requestOptions: {
      include_image_sizes: true,
      include_priority_campaign_owner: true,
      include_user_image: true,
    },
  })
  const {
    filteredPriorityCampaigns,
    hasFilteredPriorityCampaigns,
    loading: loadingPriorityCampaigns,
  } = priorityCampaignsPayload

  const currentUserIsSelected = filteredPriorityCampaigns.some(priorityCampaign => (
    priorityCampaign.owner_id === currentUser.id
  ))

  const initPriorityCampaign = {
    campaign_id: campaign.id,
    owner_id: currentUserIsSelected ? undefined : currentUser.id,
    owner_type: 'User',
  }

  const priorityCampaignFormPayload = usePriorityCampaignForm(
    initPriorityCampaign,
    { customDeps: [loadingPriorityCampaigns], validateOn: 'change' },
  )

  const {
    entityState,
    entityState: {
      owner_id: ownerId,
    },
    setEntityState,
    saveEnabled,
  } = priorityCampaignFormPayload

  const createPriorityCampaignPayload = {
    callbacks: {
      action: createFn,
      afterAction: () => setEntityState({ owner_id: undefined }),
    },
    entityParams: entityState,
    toastText: 'User Added',
  }

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header callbacks={callbacks} title="Priority" titleSecondLine="Settings" />

      <SidebarModal.Content>
        <LoadingModule loading={loadingPriorityCampaigns} />

        {!loadingPriorityCampaigns && (
          <Form>
            <EntitySelector
              callbacks={{
                selectItem: user => setEntityState({
                  owner_id: user ? user.id : null,
                }),
              }}
              entityKey="users"
              entityTitleKey="full_name"
              requestOptions={{
                include_image_sizes: true,
                include_user_image: true,
              }}
              searchRequestOptions={{
                actable_types: ['OfficeAdministrator'],
                exclude_brand_users: true,
              }}
              selectedItemId={ownerId}
              showEntityId={false}
              selectedIds={filteredPriorityCampaigns.map(priorityCampaign => priorityCampaign.owner_id)}
            />

            <Button
              buttonStyle="secondaryEdit"
              disabled={!saveEnabled}
              icon={<FontAwesomeIcon icon={faStar} />}
              loading={creating}
              marginTop="medium"
              onClick={() => createPriorityCampaign(createPriorityCampaignPayload)}
              size="medium"
            >
              Add User
            </Button>

            {hasFilteredPriorityCampaigns && (
              // @ts-expect-error TS2741
              <Form.Field
                label="Selected Users"
                labelProps={{ marginBottom: 0 }}
                marginTop="large"
              />
            )}

            {filteredPriorityCampaigns.map(priorityCampaign => (
              <PriorityCampaignUserListItem
                key={priorityCampaign.id}
                priorityCampaign={priorityCampaign}
              />
            ))}
          </Form>
        )}
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="secondaryUtility"
          onClick={() => closeModal()}
          size="large"
        >
          Close Modal
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <ManageCampaignPrioritySettingsModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
